import React, { createContext, useContext } from "react";
import { cn } from "../../styles";
import { mdiPencil, mdiPlusCircleOutline, mdiSquareEditOutline } from "@mdi/js";
import {
  ButtonVariant,
  Button,
  ButtonProps,
  Typography,
  LinkButton,
  TypographyContent,
} from "../../atoms";
import { DragItem } from "../../molecules";
import { DragList, DragListItemProps, DragListProps } from "../../layouts";
import { ThemeColor, BaseElementProps, PanelColor } from "../../props";

const EditingContext = createContext(false);

export interface AppDrawerLinkProps<T>
  extends
    ButtonProps,
    Partial<Omit<DragListItemProps<T>, "children" | "data">> {
  /** Data associated with the link for drag-and-drop operations. */
  dragData?: T;

  /** Whether this link is currently active/selected. Adds visual highlight. */
  active?: boolean;

  /** Callback triggered when the edit button is clicked in editing mode. */
  onEdit?: () => void;
}

/**
 * Navigation link component for AppDrawer with support for active states and drag-and-drop.
 * Renders as a styled link button with left border indicator when active. In editing mode,
 * transforms into a draggable item with edit functionality. Automatically adapts appearance
 * based on the editing context provided by AppDrawer.Navigation.
 */
export function AppDrawerLink<T = unknown>({
  active = false,
  id,
  index,
  dragData,
  onEdit,
  ...props
}: AppDrawerLinkProps<T>) {
  const editing = useContext(EditingContext);
  if (editing)
    return (
      <DragList.Item
        id={id}
        index={index}
        data={dragData}
        portalClassname="dark"
      >
        {(dragProps) => (
          <DragItem
            {...dragProps}
            color={PanelColor.level4}
            onAction={onEdit}
            buttonProps={{ iconPath: mdiPencil }}
            dense
          >
            <div className="grow p-1 text-xs font-normal flex items-center">
              {props.children}
            </div>
          </DragItem>
        )}
      </DragList.Item>
    );
  return (
    <LinkButton
      variant={ButtonVariant.full}
      color={ThemeColor.gray}
      className={cn(
        "border-l-2 text-md !font-normal focus-visible:ring-inset",
        active
          ? "border-primary-500 opacity-90"
          : "border-back-800 opacity-60 hover:text-primary-100",
      )}
      {...props}
    />
  );
}

export interface AppDrawerGroupProps<T>
  extends
    BaseElementProps,
    Partial<DragListProps<T>>,
    Partial<Omit<DragListItemProps<T>, "children" | "data">> {
  /** Navigation links and other content within this group. */
  children: React.ReactNode;

  /** Group title displayed as uppercase header. Supports localized content. */
  title: TypographyContent;

  /** Label for the add link button shown in editing mode. Supports localized content. */
  addLink: TypographyContent;

  /** Callback triggered when the add link button is clicked in editing mode. */
  onAddLink: () => void;

  /** Callback triggered when the group edit button is clicked in editing mode. */
  onEdit: () => void;

  /** Data associated with the group for drag-and-drop operations. */
  dragData?: T;
}

/**
 * Collapsible navigation group component for organizing related links in the AppDrawer.
 * Displays a title header with optional edit button and contains AppDrawer.Link children.
 * In editing mode, becomes draggable with add/edit controls for dynamic navigation management.
 * Groups are visually separated and styled with uppercase titles in the primary color.
 */
export function AppDrawerGroup<T = unknown>({
  id,
  index,
  dragData,
  className,
  title = "Title",
  addLink = "AddLink",
  onAddLink,
  onEdit,
  onDragListChange,
  children,
}: AppDrawerGroupProps<T>) {
  const editing = useContext(EditingContext);
  if (editing)
    return (
      <DragList.Item
        id={id}
        index={index}
        data={dragData}
        portalClassname="dark"
      >
        {(dragProps) => (
          <DragItem
            {...dragProps}
            className="bg-back-800"
            color={PanelColor.level3}
          >
            <div className="p-2 grow-1 flex flex-col gap-2">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold tracking-widest uppercase text-primary-500 dark:text-primary-600 select-none">
                  <Typography.Span color={ThemeColor.primary} content={title} />
                </div>
                {onEdit && (
                  <Button
                    variant={ButtonVariant.text}
                    color={ThemeColor.primary}
                    muted
                    iconPath={mdiSquareEditOutline}
                    onClick={onEdit}
                  />
                )}
              </div>

              <DragList
                id={id}
                type="pss-drawer-group"
                className="space-y-3 min-h-8"
                onDragListChange={onDragListChange}
              >
                {children}
              </DragList>
              {onAddLink && (
                <Button
                  variant={ButtonVariant.outlined}
                  color={ThemeColor.gray}
                  dense
                  centered
                  iconPath={mdiPlusCircleOutline}
                  onClick={onAddLink}
                >
                  <Typography.Span content={addLink} />
                </Button>
              )}
            </div>
          </DragItem>
        )}
      </DragList.Item>
    );
  return (
    <div className={cn("mt-4 mb-4", className)}>
      <Typography.Span
        className="block px-4 mb-2 text-sm font-semibold tracking-widest uppercase text-primary-500 dark:text-primary-600 select-none"
        content={title}
      />
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

export interface AppDrawerNavigationProps<T>
  extends BaseElementProps, Partial<DragListProps<T>> {
  /** Enables editing mode with drag-and-drop and add/edit controls. */
  editing: boolean;

  /** Navigation groups and links to display. */
  children: React.ReactNode;

  /** Label for the add group button shown in editing mode. Supports localized content. */
  addGroup: TypographyContent;

  /** Callback triggered when the add group button is clicked in editing mode. */
  onAddGroup: () => void;
}

/**
 * Main navigation container for AppDrawer that manages editing state and drag-and-drop functionality.
 * Contains AppDrawer.Group and AppDrawer.Link components. In editing mode, enables full reordering
 * with drag-and-drop, group management, and link creation. Provides editing context to all child
 * components to coordinate their display modes. Width adapts based on editing state.
 */
export function AppDrawerNavigation<T = unknown>({
  id = "pss-drawer-nav",
  type = "pss-drawer-nav",
  editing = false,
  children,
  addGroup = "AddGroup",
  onAddGroup,
  onDragListChange,
  className,
}: AppDrawerNavigationProps<T>) {
  return (
    <nav
      className={cn(
        "flex flex-col relative",
        editing ? "min-w-[300px]" : "min-w-[224px]",
        className,
      )}
    >
      {editing ? (
        <div className="p-3 space-y-4">
          <DragList
            id={id}
            type={type}
            className="space-y-3"
            onDragListChange={onDragListChange}
          >
            <EditingContext.Provider value={true}>
              {children}
            </EditingContext.Provider>
          </DragList>
          {onAddGroup && (
            <Button
              className="w-full"
              variant={ButtonVariant.outlined}
              color={ThemeColor.gray}
              centered
              dense
              iconPath={mdiPlusCircleOutline}
              onClick={onAddGroup}
            >
              <Typography.Span content={addGroup} />
            </Button>
          )}
        </div>
      ) : (
        children
      )}
    </nav>
  );
}

export interface AppDrawerCopyrightProps {
  /** Copyright text or company name to display.
   * @default "Peak Solution GmbH"
   */
  children?: React.ReactNode;
}

/**
 * Footer copyright notice component for AppDrawer.
 * Displays centered, muted copyright text at the bottom of the drawer.
 * Automatically includes © symbol before the provided content.
 */
export const AppDrawerCopyright = ({
  children = "Peak Solution GmbH",
}: AppDrawerCopyrightProps) => (
  <div className="py-3 text-center text-xs text-front-400/50 dark:text-front-500/50">
    © {children}
  </div>
);
